import React, { useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';

import StatCard from './StatCard';
import StatusChip from './StatusChip';
import DashboardErrorBanner from './DashboardErrorBanner';
import PalletSearchField from './PalletSearchField';
import {
  getStockDashboard,
  getLatestImport,
  StockDashboardData,
} from '../../services/wmsDashboardService';

const AGE_THRESHOLD_KEY = 'wms_age_threshold';

export default function StockDashboard() {
  const [data, setData] = useState<StockDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [ageThreshold] = useState<number>(() => {
    const stored = localStorage.getItem(AGE_THRESHOLD_KEY);
    return stored ? parseInt(stored, 10) : 90;
  });
  const [prismStaleAlert, setPrismStaleAlert] = useState(false);
  const [prismStaleTime, setPrismStaleTime] = useState<string | null>(null);
  const [prismAlertDismissed, setPrismAlertDismissed] = useState(() => {
    return sessionStorage.getItem('wms_prism_alert_dismissed') === 'true';
  });
  const [secondsAgo, setSecondsAgo] = useState<number | null>(null);

  // Prism staleness check on mount
  useEffect(() => {
    const checkPrism = async () => {
      const result = await getLatestImport();
      if (result) {
        const importTime = new Date(result.imported_at).getTime();
        const now = new Date().getTime();
        const diffMs = now - importTime;
        if (diffMs > 4 * 60 * 60 * 1000) {
          setPrismStaleAlert(true);
          const hoursAgo = Math.floor(diffMs / (60 * 60 * 1000));
          if (hoursAgo < 1) {
            setPrismStaleTime('less than an hour ago');
          } else if (hoursAgo === 1) {
            setPrismStaleTime('1 hour ago');
          } else {
            setPrismStaleTime(`${hoursAgo} hours ago`);
          }
        }
      }
    };
    checkPrism();
  }, []);

  // Fetch dashboard data
  const fetchData = useCallback(async () => {
    try {
      const result = await getStockDashboard({
        search: searchTerm || undefined,
        status: statusFilter || undefined,
        age_threshold: ageThreshold,
      });
      setData(result);
      setLastUpdated(new Date());
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Unable to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, statusFilter, ageThreshold]);

  // Refetch on filter/search change
  useEffect(() => {
    setLoading(true);
    fetchData();
  }, [fetchData]);

  // Polling: 30s with visibility pause
  useEffect(() => {
    let intervalId: NodeJS.Timeout;

    const start = () => {
      intervalId = setInterval(fetchData, 30000);
    };
    const stop = () => {
      clearInterval(intervalId);
    };

    const onVisibility = () => {
      if (document.hidden) {
        stop();
      } else {
        fetchData();
        start();
      }
    };

    start();
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [fetchData]);

  // "Updated Xs ago" counter
  useEffect(() => {
    if (!lastUpdated) return;

    const tick = () => {
      setSecondsAgo(Math.floor((Date.now() - lastUpdated.getTime()) / 1000));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [lastUpdated]);

  const handleDismissPrismAlert = () => {
    setPrismAlertDismissed(true);
    sessionStorage.setItem('wms_prism_alert_dismissed', 'true');
  };

  const handleStatClick = (status: string) => {
    setStatusFilter((prev) => (prev === status ? null : status));
  };

  const handleSearch = useCallback((term: string) => {
    setSearchTerm(term);
  }, []);

  return (
    <Box sx={{ width: '100%', flexGrow: 1 }}>
      {/* Prism stale alert */}
      {prismStaleAlert && !prismAlertDismissed && (
        <Alert severity="warning" onClose={handleDismissPrismAlert} sx={{ mb: 2 }}>
          Prism data last imported {prismStaleTime}. Feed may be stale.
        </Alert>
      )}

      {/* Summary stats row — layout provides the page title */}
      {data && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(5, 1fr)' },
            gap: 1,
          }}
        >
          <StatCard
            label="In Storage"
            value={data.summary.in_storage}
            onClick={() => handleStatClick('in_storage')}
          />
          <StatCard
            label="Picked"
            value={data.summary.picked}
            onClick={() => handleStatClick('picked')}
          />
          <StatCard
            label="Staged"
            value={data.summary.staged}
            onClick={() => handleStatClick('staged')}
          />
          <StatCard
            label="Loaded"
            value={data.summary.loaded}
            onClick={() => handleStatClick('loaded')}
          />
          <StatCard
            label={`Aging (${ageThreshold}+ days)`}
            value={data.summary.aging}
            tint={data.summary.aging > 0 ? '#FFF3E0' : undefined}
            onClick={() => handleStatClick('aging')}
          />
        </Box>
      )}

      {/* Search + filter row */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2,
          mb: 3,
          mt: 3,
        }}
      >
        <PalletSearchField onSearch={handleSearch} />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <Select
            value={statusFilter || ''}
            onChange={(e) => setStatusFilter(e.target.value || null)}
            displayEmpty
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="in_storage">In Storage</MenuItem>
            <MenuItem value="picked">Picked</MenuItem>
            <MenuItem value="staged">Staged</MenuItem>
            <MenuItem value="loaded">Loaded</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Updated timestamp */}
      {secondsAgo !== null && (
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
          Updated {secondsAgo}s ago
        </Typography>
      )}

      {/* Error state */}
      {error && <DashboardErrorBanner message={error} onRetry={fetchData} />}

      {/* Loading state */}
      {loading && !data && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Pallet table */}
      {data && (
        <>
          {data.pallets.length === 0 ? (
            <Paper
              sx={{
                p: 4,
                textAlign: 'center',
                borderRadius: '12px',
              }}
            >
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                No pallets found
              </Typography>
              <Typography variant="body1" color="text.secondary">
                No pallets match your search. Try a different job number, customer code, or SSCC.
              </Typography>
            </Paper>
          ) : (
            <TableContainer component={Paper} sx={{ borderRadius: '12px' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Job Number
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Customer
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Item
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Qty
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        SSCC
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Location
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Status
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Age
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.pallets.map((row) => {
                    const ageDays = Math.floor(row.age_days);
                    return (
                      <TableRow
                        key={row.id}
                        sx={{
                          borderLeft:
                            ageDays >= 90
                              ? '4px solid #F57C00'
                              : '4px solid transparent',
                        }}
                      >
                        <TableCell>{row.job_number || '\u2014'}</TableCell>
                        <TableCell>{row.customer_name || row.customer_code || '\u2014'}</TableCell>
                        <TableCell>{row.product_description || row.stock_code || '\u2014'}</TableCell>
                        <TableCell align="right">
                          {row.quantity != null ? row.quantity.toLocaleString() : '\u2014'}
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {row.sscc}
                          </Typography>
                        </TableCell>
                        <TableCell>{row.location_code || '\u2014'}</TableCell>
                        <TableCell>
                          <StatusChip status={row.status} />
                        </TableCell>
                        <TableCell>
                          {ageDays >= 90 ? (
                            <Chip
                              label="90+ days"
                              size="small"
                              sx={{ backgroundColor: '#F57C00', color: '#fff' }}
                            />
                          ) : (
                            `${ageDays}d`
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}
    </Box>
  );
}
